import React from "react";

interface OtpInputProps {
  value?: string;
  onChange?: (otp: string) => void;
  length?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
}

const OtpInput: React.FC<OtpInputProps> = ({
  value = "",
  onChange,
  length = 6,
  disabled = false,
  autoFocus = true,
  className,
}) => {
  const [digits, setDigits] = React.useState<string[]>(Array(length).fill(""));
  const refs = React.useRef<Array<HTMLInputElement | null>>([]);

  React.useEffect(() => {
    if (typeof value !== "string") return;
    const next = Array(length)
      .fill("")
      .map((_, i) => value[i] || "");
    setDigits(next);
  }, [value, length]);

  React.useEffect(() => {
    if (autoFocus && refs.current[0]) {
      refs.current[0].focus();
      refs.current[0].select?.();
    }
  }, [autoFocus]);

  const emit = (arr: string[]) => {
    const joined = arr.join("");
    onChange?.(joined);
  };

  const handleChange = (index: number, val: string) => {
    const digit = (val.match(/\d/) || [""])[0];
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    emit(next);
    if (digit && index < length - 1) {
      refs.current[index + 1]?.focus();
      refs.current[index + 1]?.select?.();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (digits[index]) {
        const next = [...digits];
        next[index] = "";
        setDigits(next);
        emit(next);
      } else if (index > 0) {
        const next = [...digits];
        next[index - 1] = "";
        setDigits(next);
        emit(next);
        refs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      refs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < length - 1) {
      refs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (index: number, e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text");
    const onlyDigits = (text.match(/\d/g) || []).slice(0, length - index);
    if (onlyDigits.length === 0) return;
    const next = [...digits];
    for (let i = 0; i < onlyDigits.length; i++) {
      next[index + i] = onlyDigits[i];
    }
    setDigits(next);
    emit(next);
    const focusTo = Math.min(index + onlyDigits.length, length - 1);
    refs.current[focusTo]?.focus();
  };

  return (
    <div
      className={className}
      style={{
        display: "flex",
        gap: 8,
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={digits[i] || ""}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={(e) => handlePaste(i, e)}
          disabled={disabled}
          style={{
            width: 44,
            height: 48,
            textAlign: "center",
            fontSize: 20,
            borderRadius: 8,
            border: "1px solid #e5e7eb",
            outline: "none",
            background: disabled ? "#f3f4f6" : "#fff",
          }}
          onFocus={(e) => e.currentTarget.select?.()}
        />
      ))}
    </div>
  );
};

export default OtpInput;
